import React, { forwardRef, useCallback, useEffect, useState } from 'react'
import Text from 'core/elements/Text'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import { useDropzone } from 'react-dropzone'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import withFormContext from 'core/components/validatedForm/withFormContext'
import SimpleLink from 'core/components/SimpleLink'
import colors from 'core/themes/base/colors'
import { defaultAppIconPath } from './constant'

interface Props {
  onChange: (value: any) => void
  onChangeName?: (value: string) => void
  fileTypes: string[]
  value?: any
  id?: string
  validations?: any[]
  hasError?: boolean
  errorMessage?: string
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    // height: 128,
    gap: 16,
  },
  previewBox: {
    width: 150,
    height: 150,
    border: `1px solid ${theme.components.dropdown.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    overflow: 'hidden',
    flexShrink: 0,
  },
  previewImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'cover',
  },
  dropzone: {
    padding: '16px',
    minHeight: 118,
    background: theme.components.card.background,
    border: `1px dashed ${theme.components.dropdown.border}`,
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    position: 'relative',
    flex: 1,
  },
  uploadIcon: {
    color: theme.components.card.passiveText,
  },
  iconContainer: {
    background: theme.components.card.activeBackground,
    width: 40,
    height: 40,
    borderRadius: 20,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    width: 25,
  },
  dropzoneText: {
    marginTop: 0,
  },
  trash: {
    position: 'absolute',
    top: 12,
    right: 12,
    color: theme.components.card.passiveText,
    cursor: 'pointer',
  },
  errorText: {
    color: theme.components.graph.error,
  },
  loadedFile: {
    marginTop: 0,
  },
  dropdownLink: {
    padding: theme.spacing(1, 0),
    transition: 'color .2s ease',
    color: colors.blue[400],
    lineHeight: '36px',
    // '&:hover': {
    //   color: theme.components.typography.active,
    // },
  },
  appInfoFooterTitle: {
    color: theme.components.typography.passive,
    textTransform: 'uppercase',
    padding: theme.spacing(0, 6.5),
  },
  appIcon: {
    display: 'grid',
    gap: theme.spacing(1),
  },
}))

const Warning = ({ children }) => {
  const classes = useStyles({})
  return (
    <Text variant="body1" className={classes.errorText}>
      {children}
    </Text>
  )
}

const ImageUpload: React.FC<Props> = forwardRef<HTMLElement, Props>((props, ref) => {
  const [fileName, setFileName] = useState('')
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const { onChange, onChangeName, fileTypes = [], value, hasError, errorMessage, id } = props

  const classes = useStyles({})

  useEffect(() => {
    if (!fileName && value?.name) {
      setFileName(value.name)
      setPreviewUrl(URL.createObjectURL(value))
    }
  }, [fileName, value])

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      if (file) {
        setFileName(file.name)
        onChange(file)
        onChangeName?.(file.name)
        setPreviewUrl(URL.createObjectURL(file))
      }
    },
    [onChange, onChangeName],
  )

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    accept: fileTypes.join(','),
    maxFiles: 1,
  })

  return (
    <div id={id}>
      <div className={classes.appIcon}>
        <div className={classes.container}>
          <div className={classes.previewBox}>
            {previewUrl ? (
              <img src={previewUrl} alt="Preview" className={classes.previewImage} />
            ) : (
              <img src={defaultAppIconPath} alt="Preview" className={classes.previewImage} />
            )}
          </div>

          <div className={classes.dropzone} {...getRootProps()}>
            <input {...getInputProps()} />
            <div>
              <div className={classes.iconContainer}>
                <img src="/ui/images/cloud-upload@3x.png" className={classes.icon} />
              </div>
              {!!fileName && (
                <div className={classes.loadedFile}>
                  <Text variant="caption1">
                    <FontAwesomeIcon>check</FontAwesomeIcon> {fileName}
                  </Text>
                </div>
              )}
              <div className={classes.dropzoneText}>
                <Text variant="caption1">
                  <SimpleLink onClick={open}>
                    <Text variant="caption1" className={classes.dropdownLink}>
                      Click to upload
                    </Text>
                    {/* Click to upload */}
                  </SimpleLink>{' '}
                  <b>or drag and drop</b>
                </Text>
                <Text variant="body2">Only .jpg and .png files are supported</Text>
              </div>
            </div>
          </div>
        </div>
        <div className={classes.container}>
          <Text variant="caption2" className={classes.appInfoFooterTitle}>
            PREVIEW
          </Text>
        </div>
      </div>
      {hasError && <Warning>{errorMessage}</Warning>}
    </div>
  )
})

export default withFormContext(ImageUpload)
